/**
 * People → Talent Acquisition → "Búsquedas solicitadas": el repositorio de
 * búsquedas (spec §7) para el recruiter — una fila por sesión de intake con
 * puesto, área, Hiring Manager, estado, fechas, rondas de cambios y recruiter
 * asignado. Filtros por estado (por defecto las guardadas y en proceso) y
 * búsqueda por texto; el detalle abre JD, Sourcing Guide, intake y transcripción.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Search } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelectPopover } from '@/components/ui/multi-select-popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTaIntakeList } from '@/hooks/useTalentAcquisition';
import { usePagination } from '@/hooks/usePagination';
import { formatDate } from '@/lib/utils';
import { TA_INTAKE_STATUSES, type TaIntakeSessionSummary, type TaIntakeStatus } from '@/../../shared/types/ta-intake';
import { TaIntakeDetailDialog } from './TaIntakeDetailDialog';
import { TaIntakeStatusBadge } from './TaIntakeStatusBadge';

const ESTADOS_DEFAULT: TaIntakeStatus[] = ['SAVED', 'IN_PROCESS'];

export function TaIntakeRequestsTab() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.intake.requests';
  const list = useTaIntakeList();
  const [estados, setEstados] = useState<string[]>(ESTADOS_DEFAULT);
  const [q, setQ] = useState('');
  const [detalle, setDetalle] = useState<string | null>(null);

  const todas = useMemo(() => list.data ?? [], [list.data]);
  const filtradas = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return todas.filter((s) => {
      if (estados.length && !estados.includes(s.status)) return false;
      if (needle && !`${s.jobTitle} ${s.areaTeam} ${s.hmName} ${s.hmEmail} ${s.assignedRecruiter ?? ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [todas, estados, q]);
  const { pageItems: paginaActual, footer: paginationFooter } = usePagination(filtradas, 25);

  const opciones = TA_INTAKE_STATUSES.filter((s) => s !== 'ABANDONED').map((s) => ({
    value: s, label: t(`talent_acquisition.intake.status.${s}`), count: todas.filter((x) => x.status === s).length,
  }));

  if (list.isLoading) return <div className="flex items-center justify-center py-16"><WinclapLoader size={32} /></div>;
  if (list.error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t(`${k}.load_error`)}</AlertTitle>
        <AlertDescription>{list.error.message}</AlertDescription>
      </Alert>
    );
  }

  const Fila = ({ s }: { s: TaIntakeSessionSummary }) => (
    <TableRow className="cursor-pointer" onClick={() => setDetalle(s.sessionId)}>
      <TableCell className="font-medium">{s.jobTitle || t('talent_acquisition.intake.chat.untitled')}</TableCell>
      <TableCell className="text-muted-foreground">{s.areaTeam || '—'}</TableCell>
      <TableCell>{s.hmName || s.hmEmail}</TableCell>
      <TableCell><TaIntakeStatusBadge status={s.status} /></TableCell>
      <TableCell className="text-muted-foreground">{formatDate(s.createdAt)}</TableCell>
      <TableCell className="text-muted-foreground">{s.approvedAt ? formatDate(s.approvedAt) : '—'}</TableCell>
      <TableCell className="text-center">{s.jdVersions ? s.jdRevisions : '—'}</TableCell>
      <TableCell className="text-muted-foreground">{s.assignedRecruiter ?? '—'}</TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t(`${k}.desc`)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectPopover label={t(`${k}.filter_status`)} options={opciones} selected={estados} onChange={setEstados} allLabel={t(`${k}.all_statuses`)} />
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t(`${k}.search`)} className="h-9 w-64 pl-8" />
        </div>
        <span className="text-xs text-muted-foreground">{t(`${k}.counter`, { count: filtradas.length })}</span>
        <Button size="sm" variant="ghost" className="ml-auto gap-1.5" onClick={() => list.refetch()} disabled={list.isFetching}>
          {list.isFetching ? <WinclapLoader size={14} /> : <RefreshCw className="h-3.5 w-3.5" />}{t('talent_acquisition.refresh')}
        </Button>
      </div>
      {filtradas.length === 0 ? (
        <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">{t(`${k}.empty`)}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(`${k}.col_position`)}</TableHead>
                <TableHead>{t(`${k}.col_area`)}</TableHead>
                <TableHead>{t(`${k}.col_hm`)}</TableHead>
                <TableHead>{t(`${k}.col_status`)}</TableHead>
                <TableHead>{t(`${k}.col_created`)}</TableHead>
                <TableHead>{t(`${k}.col_approved`)}</TableHead>
                <TableHead className="text-center">{t(`${k}.col_revisions`)}</TableHead>
                <TableHead>{t(`${k}.col_recruiter`)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{paginaActual.map((s) => <Fila key={s.sessionId} s={s} />)}</TableBody>
          </Table>
        </div>
      )}
      {paginationFooter}
      <TaIntakeDetailDialog sessionId={detalle} onClose={() => setDetalle(null)} />
    </div>
  );
}
